/** Görüntüləmə köməkçiləri. */

/**
 * ⚠️ TARİX ADLARI ƏL İLƏ YAZILIB — `Intl`-ə ETİBAR ETMƏK OLMAZ.
 *
 * Chromium (yəni Chrome, Edge, Brave — bazarın böyük hissəsi) `az-AZ`
 * lokalını «dəstəklənir» kimi göstərir: `supportedLocalesOf(['az-AZ'])`
 * onu qaytarır, `resolvedOptions().locale` da `az-AZ` verir. Amma CLDR
 * məlumatında Azərbaycan AY ADLARI YOXDUR və format kök lokala düşür:
 *
 *     {day:'numeric', month:'short'}  →  «M08 6»      («6 avq» olmalıydı)
 *     {dateStyle:'long'}              →  «2026 M08 6» («6 avqust 2026»)
 *     {month:'long'}                  →  «M08»        («avqust»)
 *     {weekday:'long'}                →  «Thu»        («cümə axşamı»)
 *
 * Bu, canlı sistemdə aşkarlandı: poçt siyahısında hər tarix «M08 6»
 * görünürdü. Node-da eyni kod düzgün işləyir (tam ICU var), ona görə
 * yalnız brauzerdə görünürdü və sınaqdan yayınmışdı.
 *
 * ⚠️ SAAT (hour/minute) İSTİSNADIR — rəqəm formatı lokal məlumat tələb
 * etmir və düzgün işləyir. Ona görə yalnız ay/gün adları burada saxlanılır.
 *
 * ⚠️ YENİ TARİX FORMATI ƏLAVƏ EDƏNDƏ `Intl.DateTimeFormat('az-AZ')`
 * YAZMA — bu cədvəllərdən istifadə et.
 */
const MONTHS_SHORT = [
  'yan', 'fev', 'mar', 'apr', 'may', 'iyn',
  'iyl', 'avq', 'sen', 'okt', 'noy', 'dek',
] as const;

const MONTHS_LONG = [
  'yanvar', 'fevral', 'mart', 'aprel', 'may', 'iyun',
  'iyul', 'avqust', 'sentyabr', 'oktyabr', 'noyabr', 'dekabr',
] as const;

/** `getDay()` sırası: 0 = bazar. */
const WEEKDAYS = [
  'bazar', 'bazar ertəsi', 'çərşənbə axşamı', 'çərşənbə',
  'cümə axşamı', 'cümə', 'şənbə',
] as const;

/** Saat:dəqiqə — rəqəm formatıdır, lokal məlumat tələb etmir. */
function hhmm(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * Siyahıdakı tarix: bu gün → saat, bu il → «6 avq», köhnə → «6 avq 2025».
 * Yandex referansındakı davranış (docs/05-panel-ui.md).
 */
export function listDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';

  const now = new Date();
  const sameDay =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();

  if (sameDay) return hhmm(d);
  const dm = `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
  return d.getFullYear() === now.getFullYear() ? dm : `${dm} ${d.getFullYear()}`;
}

/** Tam tarix — məktubu açanda başlıqda: «6 avqust 2026, 06:50». */
export function fullDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}, ${hhmm(d)}`;
}

/**
 * Sitat və yönləndirmə başlığı üçün — həftə günü ilə.
 * «cümə axşamı, 6 avqust 2026, 06:50»
 *
 * ⚠️ BU MƏTN GÖNDƏRİLƏN MƏKTUBUN İÇİNƏ DÜŞÜR. Əvvəl burada
 * `toLocaleString('az')` işlədilirdi və qarşı tərəf «2026-08-06 06:50:00»
 * görürdü — Azərbaycanca deyil, maşın formatı.
 */
export function quoteDate(iso: string | null): string {
  const d = iso ? new Date(iso) : new Date();
  if (Number.isNaN(d.getTime())) return '';
  return `${WEEKDAYS[d.getDay()]}, ${d.getDate()} ${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}, ${hhmm(d)}`;
}

/** Yalnız saat — «Qaralama saxlanıldı 21:07» kimi yerlərdə. */
export function timeOnly(date: Date): string {
  return hhmm(date);
}

/**
 * Say — minlik ayırıcısı ilə: 8694 → «8.694».
 *
 * ⚠️ `Intl.NumberFormat('az-AZ')` İŞLƏMİR — eyni ICU boşluğu.
 * Chromium `az-AZ` üçün «1,234.5» verir, halbuki Azərbaycan formatı
 * «1.234,5»-dir (minlik nöqtə, onluq vergül). Yəni ayırıcılar TƏRSİNƏ
 * gəlir və rəqəm yanlış oxunur.
 *
 * Niyə lazımdır: qovluqda «8694» yazısı bir baxışda oxunmur, «8.694»
 * isə oxunur. Gmail və Yandex hər ikisi ayırıcı işlədir.
 */
export function fmtCount(n: number): string {
  if (!Number.isFinite(n)) return '0';
  const neg = n < 0;
  const s = Math.abs(Math.trunc(n)).toString();
  let out = '';
  for (let i = 0; i < s.length; i++) {
    // Sondan sayıb hər üç rəqəmdən bir nöqtə qoyulur.
    if (i > 0 && (s.length - i) % 3 === 0) out += '.';
    out += s[i];
  }
  return neg ? `-${out}` : out;
}

export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
}

/** Göndərənin baş hərfləri — avatar üçün. */
export function initials(name: string, address: string): string {
  const source = name.trim() || address.split('@')[0] || '?';
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

/**
 * Ünvandan sabit rəng. Eyni göndərən həmişə eyni rəngdə görünür —
 * bu, siyahını gözlə taramağı asanlaşdırır.
 */
/**
 * Avatar rəngi — SEÇİLMİŞ ÇALAR HALQASINDAN.
 *
 * ⚠️ ƏVVƏL 360 ÇALARIN HAMISI İŞLƏDİLİRDİ (`hash % 360`). İki nəticəsi
 * vardı və hər ikisi görünürdü:
 *
 *   1. Sarı avatarlar brendin amber rəngi (#f5b301) ilə yanaşı düşəndə
 *      titrəyir — göz hansının vurğu, hansının məzmun olduğunu ayırd edə
 *      bilmir.
 *   2. Qonşu sətirlərdə təsadüfi neon çalarlar yan-yana düşürdü; siyahı
 *      səliqəsiz və ucuz görünürdü.
 *
 * İndi 10 çalarlıq halqa var. 35–55 aralığı QƏSDƏN YOXDUR — məhz orada
 * brendin sarısı yerləşir.
 */
const AVATAR_HUES = [220, 250, 280, 310, 340, 355, 15, 145, 175, 200];

export function avatarHue(address: string): number {
  let hash = 0;
  for (let i = 0; i < address.length; i++) {
    // ⚠️ `>>> 0` — 32 bitlik daşmanın qarşısını alır. Onsuz uzun ünvanlarda
    // hash mənfi olur və indeks sıfıra düşür (hamısı eyni rəng).
    hash = (hash * 31 + address.charCodeAt(i)) >>> 0;
  }
  return AVATAR_HUES[hash % AVATAR_HUES.length]!;
}

/** Əlavənin tip nişanı: "application/pdf" → "PDF". */
export function attachmentKind(type: string, filename: string): string {
  const ext = filename.includes('.') ? filename.split('.').pop()! : '';
  if (ext && ext.length <= 4) return ext.toUpperCase();
  const sub = type.split('/')[1] ?? 'FILE';
  return sub.slice(0, 4).toUpperCase();
}
